import React from 'react';
import './Popup.css'; // Assuming you'll use a separate CSS file for styles

interface PopupProps {
  isModalVisible: boolean;
  closeModal: () => void;
  popupTitle: string;
  popupMessage: string;
}

// Helper function to detect and wrap URLs in anchor tags
const formatMessageWithLinks = (message: string) => {
  // Updated regex: includes spaces until a line break or double space or punctuation likely to break URL
  const urlRegex = /(https?:\/\/[^\n\r]+)/g;

  const parts: (string | JSX.Element)[] = [];
  let lastIndex = 0;
  let match;

  while ((match = urlRegex.exec(message)) !== null) {
    if (match.index > lastIndex) {
      parts.push(message.slice(lastIndex, match.index));
    }

    const rawUrl = match[0].trim(); // Still safe to trim
    const encodedUrl = encodeURI(rawUrl); // Encode space, etc.

    parts.push(
      <a href={encodedUrl} target="_blank" rel="noopener noreferrer" key={match.index}>
        {rawUrl}
      </a>
    );

    lastIndex = urlRegex.lastIndex;
  }

  if (lastIndex < message.length) {
    parts.push(message.slice(lastIndex));
  }

  // Add line breaks for nicer formatting
  const result: (string | JSX.Element)[] = [];
  parts.forEach((part, index) => {
    result.push(part);
    if (index < parts.length - 1) {
      result.push(<br key={`br-${index}`} />);
    }
  });

  return <>{result}</>;
};

const Popup: React.FC<PopupProps> = ({ isModalVisible, closeModal, popupTitle, popupMessage }) => {
  // Check if the message contains error details (has newlines and the second part looks like JSON)
  const messageParts = popupMessage.split('\n\n');
  const hasErrorDetails = messageParts.length === 2 &&
    (messageParts[1].trim().startsWith('{') || messageParts[1].trim().startsWith('['));
  const JSON_POPUP_TITLES = new Set([
    'Sync JC Pieceworks Cache',
    'Piece Import Complete',
    'Piece Import Failed',
  ]);
  const isStructuredJsonPopup = hasErrorDetails && JSON_POPUP_TITLES.has(popupTitle);

  return (
    <>
      {isModalVisible && (
        <div className="modalOverlay">
          <div className="modalContent">
            <h4>{popupTitle}</h4>
            {hasErrorDetails ? (
              <div className="errorMessageContainer">
                <p className={isStructuredJsonPopup ? 'responseSummary' : 'errorSummary'}>{messageParts[0]}</p>
                <div className="errorDetailsContainer">
                  {!isStructuredJsonPopup && <h5>Error Details:</h5>}
                  <div className="errorDetailsContent">
                    {messageParts[1]}
                  </div>
                </div>
              </div>
            ) : (
              <p className="modalMessage">{formatMessageWithLinks(popupMessage)}</p>
            )}
            <button className="modalButton" onClick={closeModal}>Close</button>
          </div>
        </div>
      )}
    </>
  );
};

export default Popup;
